import * as React from 'react';
import { Navbar, Nav, NavDropdown, Dropdown } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { Auth } from 'aws-amplify';
import { inject, observer } from 'mobx-react';
import { UserProfileUIStore } from './stores/user-profile-ui-store';
import { GenericModal } from './components/common/generic-modal';
import { SettingsAnnouncement } from './components/common/settings-announcement';
import { DemoVideoModal } from './components/common/demo-video-modal';
import { WhatsNewModal, WHATS_NEW_ITEMS, WhatsNewItem } from './components/common/whats-new';
import { ClinicalReviewLink } from './components/common/clinical-review-link';
import { signOutAndReload } from './utils/signOut';
import './navigation.css';

// The full evergreen tour. Bump this ONLY on a major redesign when you re-record
// the whole demo, so the auto-popup re-appears for everyone next login (and also
// update DEMO_VIDEO_YOUTUBE_ID in demo-video-modal.tsx). For per-feature updates,
// don't touch this. Add a short clip to WHATS_NEW_ITEMS in whats-new.tsx instead.
//
// Bumped to 2026-09 for the re-recorded tour covering the features released to
// prod on 2026-08-31 and 2026-09-02. The key is per browser, so a practitioner
// who dismissed 2026-07 sees this one once on their next login.
const DEMO_VIDEO_VERSION = 'demo-video-2026-09';

// Whether the full tour auto-pops on login for anyone who hasn't seen it. It was
// off between 2026-09-01 and the re-record, because the tour it would have shown
// lagged the current UI and the people it auto-shows to are new sign-ups (plus
// anyone on a fresh browser), i.e. exactly the audience a dated first impression
// costs the most. Back on now that the video matches the shipped product. Turn it
// off again the moment the recording falls behind the UI.
const AUTO_POPUP_DEMO_VIDEO: boolean = true;

// The day this re-recorded tour went live. Anyone whose profile predates it is an
// existing practitioner being re-shown the tour rather than a new sign-up meeting
// it for the first time, whatever their browser happens to remember. Move this
// forward with DEMO_VIDEO_VERSION on the next re-record.
const DEMO_VIDEO_RELEASE_DATE = '2026-09-03';

type State = {
  currentUsername: string;
  currentEmail: string;
  showUpgradeModal: boolean;
  showDemoVideo: boolean;
  demoVideoReturning: boolean;
  showWhatsNew: boolean;
  whatsNewItems: WhatsNewItem[];
};

interface NavigationProps {
  userProfileUIStore?: UserProfileUIStore;
}

// TODO: Figure out how we can just use the currentUser on the UserStore instead of
// what we are doing here with currentUsername
@inject('userProfileUIStore')
@observer
export class Navigation extends React.Component<NavigationProps, State> {
  state: State = {
    currentUsername: '',
    currentEmail: '',
    showUpgradeModal: false,
    showDemoVideo: false,
    demoVideoReturning: false,
    showWhatsNew: false,
    whatsNewItems: [],
  };

  // Guards the one-time auto-popup check so it only fires once per mount, after the
  // user profile (needed for the per-user localStorage key) has loaded.
  autoChecked = false;

  componentDidMount() {
    this.obtainCurrentUsername();
    // Load user profile for menu rendering
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore && !userProfileUIStore.currentUserProfile) {
      userProfileUIStore.loadCurrentUserProfile();
    }
    this.maybeAutoPopup();
  }

  componentDidUpdate() {
    // The profile loads asynchronously; retry the auto-show once its id is available.
    this.maybeAutoPopup();
  }

  currentUserProfileId(): string | undefined {
    return this.props.userProfileUIStore?.currentUserProfile?.id;
  }

  demoVideoStorageKey(): string | null {
    const userProfileId = this.currentUserProfileId();
    return userProfileId ? `${userProfileId}-${DEMO_VIDEO_VERSION}` : null;
  }

  // True when the popup is a re-show for an existing practitioner rather than a
  // first-run welcome. It is the only thing the modal's copy branches on.
  //
  // Two signals, because neither covers the whole audience alone. signupDate is
  // server-side, so it still identifies an existing practitioner on a browser that
  // has never seen a tour, but postSignUp only started stamping it at some point,
  // and a profile older than that carries none. The localStorage keys cover those.
  isReturningPractitioner(userProfileId: string): boolean {
    const signupDate = this.props.userProfileUIStore?.currentUserProfile?.signupDate;
    // Both sides are ISO-8601 with the date first, so a lexical compare is a date
    // compare, and a bare yyyy-mm-dd sorts before every timestamp on that same day.
    if (signupDate && signupDate < DEMO_VIDEO_RELEASE_DATE) {
      return true;
    }
    return this.seenAnEarlierDemo(userProfileId);
  }

  // Whether they dismissed an EARLIER version of the tour in this browser. Every
  // version writes its own `<profileId>-demo-video-<yyyy-mm>` key and none are ever
  // cleared, so the presence of any key but the current one is the whole test.
  // Read when the modal opens rather than on every render, since localStorage holds
  // a key per practitioner per version plus the What's New feed.
  seenAnEarlierDemo(userProfileId: string): boolean {
    const prefix = `${userProfileId}-demo-video-`;
    const currentKey = `${userProfileId}-${DEMO_VIDEO_VERSION}`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key !== currentKey && key.startsWith(prefix) && localStorage.getItem(key) === 'true') {
        return true;
      }
    }
    return false;
  }

  whatsNewStorageKey(userProfileId: string, itemId: string): string {
    return `${userProfileId}-whatsnew-${itemId}`;
  }

  isWhatsNewSeen(userProfileId: string, itemId: string): boolean {
    return localStorage.getItem(this.whatsNewStorageKey(userProfileId, itemId)) === 'true';
  }

  // Decides which single popup (if any) to auto-show on login. The full tour and the
  // What's New feed are mutually exclusive so they never stack on top of each other:
  //   - Haven't seen the current full tour  -> show the tour, and treat all current
  //     What's New items as already covered (a first-timer doesn't need "what changed").
  //   - Seen the tour -> show only the What's New clips they haven't seen yet.
  // While AUTO_POPUP_DEMO_VIDEO is false the tour branch is skipped entirely, so
  // everyone falls through to the What's New check. That suppression only existed to
  // stop two modals stacking, and with no tour popping there is nothing to stack.
  maybeAutoPopup = () => {
    if (this.autoChecked) {
      return;
    }
    const userProfileId = this.currentUserProfileId();
    if (!userProfileId) {
      // Profile not loaded yet; componentDidUpdate will retry once it is.
      return;
    }
    this.autoChecked = true;

    const demoSeen = localStorage.getItem(`${userProfileId}-${DEMO_VIDEO_VERSION}`) === 'true';
    if (AUTO_POPUP_DEMO_VIDEO && !demoSeen) {
      WHATS_NEW_ITEMS.forEach(item =>
        localStorage.setItem(this.whatsNewStorageKey(userProfileId, item.id), 'true')
      );
      this.setState({
        showDemoVideo: true,
        demoVideoReturning: this.isReturningPractitioner(userProfileId),
      });
      return;
    }

    const unseen = WHATS_NEW_ITEMS.filter(item => !this.isWhatsNewSeen(userProfileId, item.id));
    if (unseen.length > 0) {
      this.setState({ showWhatsNew: true, whatsNewItems: unseen });
    }
  };

  openDemoVideo = () => {
    const userProfileId = this.currentUserProfileId();
    this.setState({
      showDemoVideo: true,
      demoVideoReturning: userProfileId ? this.isReturningPractitioner(userProfileId) : false,
    });
  };

  closeDemoVideo = () => {
    const key = this.demoVideoStorageKey();
    if (key) {
      localStorage.setItem(key, 'true');
    }
    this.setState({ showDemoVideo: false });
  };

  openWhatsNew = () => {
    // Manual open from the menu shows the full feed so they can re-watch anything.
    this.setState({ showWhatsNew: true, whatsNewItems: WHATS_NEW_ITEMS });
  };

  closeWhatsNew = () => {
    const userProfileId = this.currentUserProfileId();
    if (userProfileId) {
      this.state.whatsNewItems.forEach(item =>
        localStorage.setItem(this.whatsNewStorageKey(userProfileId, item.id), 'true')
      );
    }
    this.setState({ showWhatsNew: false });
  };

  obtainCurrentUsername = async () => {
    console.log('calling Auth.currentSession()');
    const session: any = await Auth.currentSession();
    console.log(session);
    const currentUsername = session.idToken.payload.given_name
      ? `${session.idToken.payload.given_name} ${session.idToken.payload.family_name}`
      : session.idToken.payload.email;
    this.setState({
      currentUsername,
      currentEmail: session.idToken.payload.email || '',
    });
  };

  /**
   * The name shown at the top of the Account menu.
   *
   * The profile wins over the session token because the profile is what Profile
   * edits — nothing writes back to the Cognito attributes, so an edit would
   * otherwise appear to do nothing. The token is the fallback for practitioners
   * who have never opened Profile (given_name is only populated in prod), and
   * the email is the last resort, which is what this showed before.
   */
  displayName(): string {
    const profile = this.props.userProfileUIStore?.currentUserProfile;
    const fromProfile = [profile?.firstName, profile?.lastName]
      .filter(part => part && part.trim())
      .join(' ')
      .trim();

    return fromProfile || this.state.currentUsername;
  }

  displayEmail(): string {
    const profile = this.props.userProfileUIStore?.currentUserProfile;
    return profile?.email || this.state.currentEmail;
  }

  signOut = () => {
    signOutAndReload();
  };

  handleUpgradeClick = () => {
    this.setState({ showUpgradeModal: true });
  };

  /**
   * The account menu, sitting in the main nav immediately after Help.
   *
   * Plain text, no icon: its neighbours in this row (Clients, Test Results,
   * Help) are all bare labels, and an icon here would make Account read as a
   * different kind of thing than the dropdowns beside it. The avatar convention
   * that would justify one only applies when the account sits alone in the
   * right-hand corner, which is not where this lives.
   *
   * It is titled "Account" rather than the practitioner's name. The name used to
   * be the menu label, which put a person's name in a row of function nouns
   * (Home, Clients, Test Results, Help) where it read as a label rather than a
   * control. It moves into the menu as a header instead, so it still answers
   * "who am I signed in as" without having to be clicked to find out what is
   * under it.
   *
   * Three groups, divided: who you are, what you pay for, and the way out.
   * Sign Out sits alone at the bottom so it is never a mis-click away from the
   * items above it.
   */
  renderAccountMenu = () => (
    <NavDropdown id="account-nav-dropdown" title="Account">
      <Dropdown.Header>
        <div className="account-menu-name">{this.displayName()}</div>
        <div className="account-menu-email">{this.displayEmail()}</div>
      </Dropdown.Header>
      <Dropdown.Divider />

      <LinkContainer exact to="/profile">
        <Dropdown.Item>Profile</Dropdown.Item>
      </LinkContainer>
      {/* Settings and My Markers both used to sit here. Both moved to the Test
          Results menu — see the comment there. What is left is what Account
          actually means: who you are, and what you pay for. */}

      <Dropdown.Divider />
      <Dropdown.Item href="https://labsmarts.com/my-account" target="_blank">
        Manage Subscription
      </Dropdown.Item>

      <Dropdown.Divider />
      <Dropdown.Item onClick={this.signOut}>Sign Out</Dropdown.Item>
    </NavDropdown>
  );

  render() {
    const { showUpgradeModal } = this.state;
    return (
      <div>
        <Navbar bg="dark" variant="dark" collapseOnSelect>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse>
            <Nav>
              <Nav.Item>
                <Nav.Link href="/">Home</Nav.Link>
              </Nav.Item>
              <NavDropdown title="Clients" id="clients-nav-dropdown">
                <LinkContainer exact to="/client/add">
                  <Dropdown.Item>Add Client</Dropdown.Item>
                </LinkContainer>
                <LinkContainer exact to="/client/edit/manage">
                  <Dropdown.Item>Manage Clients</Dropdown.Item>
                </LinkContainer>
              </NavDropdown>

              <NavDropdown title="Test Results" id="test-results-nav-dropdown">
                <LinkContainer exact to="/bloodwork/add">
                  <Dropdown.Item>Add Test Results</Dropdown.Item>
                </LinkContainer>
                <LinkContainer exact to="/bloodwork/analyze">
                  <Dropdown.Item>Manage Test Results</Dropdown.Item>
                </LinkContainer>
                {/* Divided from the two above: those are a test's own
                    workflow, these two are what feed it. Neither used to live
                    here — My Markers and Report Settings were both under
                    Account, on the grounds that they are practitioner data.
                    But every one of Report Settings' blocks configures how a
                    REPORT renders (PDF inclusion, units, section display,
                    supplement brands, the byline and logo printed on it), and
                    My Markers is picked from while entering a test. Grouping
                    by what you are doing beats grouping by what the data
                    technically belongs to. */}
                <Dropdown.Divider />
                <LinkContainer exact to="/my-markers">
                  <Dropdown.Item>My Markers</Dropdown.Item>
                </LinkContainer>
                <LinkContainer exact to="/settings">
                  <Dropdown.Item>Report Settings</Dropdown.Item>
                </LinkContainer>
              </NavDropdown>

              <NavDropdown title="Help" id="help-nav-dropdown">
                <Dropdown.Item onClick={this.openDemoVideo}>
                  Watch Demo
                </Dropdown.Item>
                {/* Hidden for now — re-recording the full demo instead. Restore this
                    once WHATS_NEW_ITEMS has clips to show:
                <Dropdown.Item onClick={this.openWhatsNew}>
                  What&rsquo;s New
                </Dropdown.Item> */}
                <Dropdown.Item
                  href="https://labsmarts.com/resources"
                  target="_blank"
                >
                  Resources
                </Dropdown.Item>
                <Dropdown.Item
                  href="mailto:support@labsmarts.com"
                  target="_blank"
                >
                  Support
                </Dropdown.Item>
              </NavDropdown>

              {this.renderAccountMenu()}

              <SettingsAnnouncement
                userProfileId={
                  this.props.userProfileUIStore?.currentUserProfile?.id
                }
              />
            </Nav>

            <ClinicalReviewLink />
          </Navbar.Collapse>
        </Navbar>

        <GenericModal
          show={showUpgradeModal}
          title="Custom Logo Feature Not Available"
          text="Custom Logo feature not enabled."
          link="https://labsmarts.com/custom-logo-upgrade"
          linkText="Click here to upgrade."
          onHide={() => this.setState({ showUpgradeModal: false })}
        />

        <DemoVideoModal
          show={this.state.showDemoVideo}
          returning={this.state.demoVideoReturning}
          onHide={this.closeDemoVideo}
        />

        <WhatsNewModal
          show={this.state.showWhatsNew}
          items={this.state.whatsNewItems}
          onHide={this.closeWhatsNew}
        />
      </div>
    );
  }
}
